import React, { useState } from 'react';
import { format, parse, set, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, startOfWeek, getDay, addDays, endOfWeek } from 'date-fns';
import './TopBar.css';
import { FaBell } from 'react-icons/fa';
import { useDeleteNotification, useNotifications } from '../../api/notifications';
import { IoClose } from 'react-icons/io5';

const CustomDatePicker = ({ selectedDate, onDateSelect, onClose }) => {
  const currentDate = selectedDate ? parseISO(selectedDate) : new Date();
  const [viewDate, setViewDate] = useState(currentDate);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const firstDayOfMonth = getDay(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = addDays(endOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const monthNames = [
    'Јануари', 'Февруари', 'Март', 'Април', 'Мај', 'Јуни',
    'Јули', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
  ];

  const dayNames = ['Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб', 'Нед'];

  const handleDateClick = (date) => {
    onDateSelect(format(date, 'yyyy-MM-dd'));
    onClose();
  };

  const goToPreviousMonth = () => {
    setViewDate(subMonths(viewDate, 1));
  };

  const goToNextMonth = () => {
    setViewDate(addMonths(viewDate, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setViewDate(today);
    onDateSelect(format(today, 'yyyy-MM-dd'));
    onClose();
  };

  const adjustedCalendarDays = [];
  let currentWeek = [];
  calendarDays.forEach((date, index) => {
    currentWeek.push(date);
    if ((index + 1) % 7 === 0 || index === calendarDays.length - 1) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      adjustedCalendarDays.push(currentWeek);
      currentWeek = [];
    }
  });

  return (
    <div className="arm-custom-datepicker-overlay" onClick={onClose}>
      <div className="arm-custom-datepicker" onClick={(e) => e.stopPropagation()}>
        <div className="arm-datepicker-header">
          <button className="arm-month-nav" onClick={goToPreviousMonth}>‹</button>
          <span className="arm-month-year">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button className="arm-month-nav" onClick={goToNextMonth}>›</button>
        </div>

        <div className="arm-datepicker-days-header">
          {dayNames.map((day) => (
            <div key={day} className="arm-day-header">{day}</div>
          ))}
        </div>

        <div className="arm-datepicker-days">
          {adjustedCalendarDays.map((week, weekIndex) => (
            <div key={weekIndex} className="arm-calendar-week">
              {week.map((date, dayIndex) => {
                if (!date) {
                  return <div key={`empty-${weekIndex}-${dayIndex}`} className="arm-day-cell arm-empty"></div>;
                }
                const isSelected = selectedDate && isSameDay(date, parseISO(selectedDate));
                const isToday = isSameDay(date, new Date());
                const isCurrentMonth = isSameMonth(date, viewDate);

                return (
                  <button
                    key={date.toString()}
                    className={`arm-day-cell ${isSelected ? 'arm-selected' : ''} ${isToday ? 'arm-today' : ''} ${!isCurrentMonth ? 'arm-other-month' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="arm-datepicker-footer">
          <button className="arm-today-btn" onClick={goToToday}>Денес</button>
          <button className="arm-close-btn" onClick={onClose}>Затвори</button>
        </div>
      </div>
    </div>
  );
}

const TopBar = ({ 
  selectedDate, 
  setSelectedDate,
  onAddReservation,
  onAddWalkIn
}) => {
  const today = new Date();
  const currentDate = selectedDate ? parseISO(selectedDate) : today;
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const {data:notifications,isLoading} = useNotifications(); 
  const {mutate:deleteNotification} = useDeleteNotification(); 
  
  const convertDateToShow = (date) => { 
    if(!date){
      return("Внеси Датум")
    }
    date = new Date(date);
    let dateString = `${date.getDate().toString().padStart(2,'0')}/${(date.getMonth()+1).toString().padStart(2,'0')}/${date.getFullYear()}`
    return(dateString); 
  }

  const navigateDate = (days) => {
    const newDate = addDays(currentDate, days);
    setSelectedDate(format(newDate, 'yyyy-MM-dd'));
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
  };
  
  const handleDeleteNotification = (id) => {
    deleteNotification({ notificationId:id })
  };

  return (
    <div className="top-bar">
      <div className="date-navigation">
        <button 
          className="nav-button" 
          onClick={() => navigateDate(-1)}
          aria-label="Previous day"
        >
          ◄
        </button>
        
        <div className="date-picker-container">
          <div 
            className="date-picker-display"
            onClick={() => setShowDatePicker(true)}
          >
            {convertDateToShow(selectedDate)}
          </div>
        </div>
        
        <button 
          className="nav-button" 
          onClick={() => navigateDate(1)}
          aria-label="Next day"
        >
          ►
        </button>
        
        <button 
          className="today-button"
          onClick={() => setSelectedDate(format(today, 'yyyy-MM-dd'))}
        >
          Денес
        </button>
      </div>
      <div className="button-group">
        <button 
          className="notifications-button"
          onClick={toggleNotifications}
          aria-label="View notifications"
        >
          <FaBell/>
          {notifications?.length > 0 && (
            <span className="notification-counter">{notifications.length}</span>
          )}
        </button>
        <button 
          className="add-reservation-button"
          onClick={onAddReservation}
        >
          Додади резервација
        </button>
        <button 
          className="add-walkin-button"
          onClick={onAddWalkIn}
        >
          Додади влез без резервација
        </button>
      </div>

      {showDatePicker && (
        <CustomDatePicker
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {isNotificationsOpen && (
        <div className="notifications-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Известувања</h2>
              <button 
                className="close-button"
                onClick={toggleNotifications}
                aria-label="Close notifications"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {isLoading ? (
                <p>Се вчитува...</p>
              ) : notifications && notifications.length > 0 ? (
                <ul className="notifications-list">
                  {notifications.map((notification) => (
                    <li key={notification.id} className="notification-item">
                      <div className="notification-content">
                        <h3>{notification.title}</h3>
                        <p>{notification.text}</p>
                      </div>
                      <button
                        className="delete-notification-button"
                        onClick={() => handleDeleteNotification(notification.id)}
                        aria-label={`Delete notification: ${notification.title}`}
                      >
                        <IoClose/>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Нема нови известувања.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBar;